import {
  DESKTOP_WIDTH,
  GRID_ROW_HEIGHT,
  TABLET_WIDTH,
  MOBILE_WIDTH,
  getGridPlacement,
  isComponentHidden,
  sortComponentsByGrid,
} from './defaults';

export function contentWidthForViewport(viewport = 'desktop') {
  if (viewport === 'tablet') return TABLET_WIDTH;
  if (viewport === 'mobile') return MOBILE_WIDTH;
  return DESKTOP_WIDTH;
}

/** Hauteur de section en lignes de grille à partir du ratio média (largeur = section). */
export function computeGridRowsFromAspectRatio(
  mediaWidth,
  mediaHeight,
  contentWidth = DESKTOP_WIDTH,
  rowHeight = GRID_ROW_HEIGHT,
) {
  const w = Number(mediaWidth);
  const h = Number(mediaHeight);
  if (!w || !h || !contentWidth) return 16;
  const rh = rowHeight > 0 ? rowHeight : GRID_ROW_HEIGHT;
  const heightPx = contentWidth * (h / w);
  return Math.max(4, Math.min(600, Math.round(heightPx / rh)));
}

export function loadImageDimensions(url) {
  return new Promise((resolve, reject) => {
    if (!url) {
      reject(new Error('empty'));
      return;
    }
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = reject;
    img.src = url;
  });
}

export function loadVideoDimensions(url) {
  return new Promise((resolve, reject) => {
    if (!url) {
      reject(new Error('empty'));
      return;
    }
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      resolve({
        width: video.videoWidth,
        height: video.videoHeight,
      });
      video.removeAttribute('src');
      video.load();
    };
    video.onerror = reject;
    video.src = url;
  });
}

/** Lignes de grille canoniques (réf. desktop) → lignes pour le viewport courant. */
export function gridRowsForViewport(desktopRows, viewport = 'desktop') {
  const rows = Number(desktopRows);
  if (!rows || rows < 1) return 0;
  const w = contentWidthForViewport(viewport);
  return Math.max(1, Math.round(rows * (w / DESKTOP_WIDTH)));
}

/** Lignes affichées dans le viewport → lignes canoniques desktop. */
export function gridRowsToDesktopCanonical(viewportRows, viewport = 'desktop') {
  const rows = Number(viewportRows);
  if (!rows || rows < 1) return 1;
  const w = contentWidthForViewport(viewport);
  return Math.max(1, Math.round(rows * (DESKTOP_WIDTH / w)));
}

/** Champ section où stocker la hauteur (lignes) pour un viewport donné. */
export function viewportSectionGridRowsField(section, viewport = 'desktop') {
  if (viewport === 'desktop') return 'gridRows';
  if (section?.designKit === 'jardins') {
    return viewport === 'mobile' ? 'jardinsMobileGridRows' : 'jardinsTabletGridRows';
  }
  return viewport === 'mobile' ? 'mobileGridRows' : 'tabletGridRows';
}

export function applySectionGridRowsForViewport(section, viewport, rows) {
  const n = Math.max(4, Math.min(600, Number(rows) || 32));
  if (viewport === 'desktop') {
    return { ...section, gridRows: n, gridRowsCustom: true };
  }
  const field = viewportSectionGridRowsField(section, viewport);
  return { ...section, [field]: n, gridRowsCustom: true };
}

function gridRowHeightForSectionMeta(section) {
  if (section?.designKit === 'jardins') return 18;
  return GRID_ROW_HEIGHT;
}

/** Hauteur de section définie manuellement (desktop → viewport). */
export function resolveSectionConfiguredGridRows(section, viewport = 'desktop') {
  if (viewport !== 'desktop') {
    const field = viewportSectionGridRowsField(section, viewport);
    const explicit = Number(section?.[field]);
    if (explicit > 0) return explicit;
    return 0;
  }
  const n = Number(section?.gridRows);
  if (!n || n < 1) return 0;
  if (section?.gridRowsCustom || section?.designKit) {
    return gridRowsForViewport(n, viewport);
  }
  return 0;
}

function isJardinsForestFillImage(comp, section) {
  return (
    comp?.type === 'image' &&
    comp?.props?.kitStyle === 'jardins-fill' &&
    section?.designKit === 'jardins' &&
    (section.id === 'sec-j-saison' || section.kitSectionTone === 'forest')
  );
}

/**
 * Hauteur de grille d'une section — composants masqués exclus ;
 * téléphone / tablette : hauteur propre (pas de repli sur le desktop).
 */
export function resolveSectionTotalGridRows(section, viewport, components, options = {}) {
  const {
    resizePreviewRows = null,
    previewCompId = null,
    previewPlacement = null,
    incomingPreviewEnd = 0,
    ignoreConfiguredRows = false,
  } = options;

  const forestDesktopBleed =
    section?.designKit === 'jardins' &&
    viewport === 'desktop' &&
    (section.id === 'sec-j-saison' || section.kitSectionTone === 'forest');

  const visible = sortComponentsByGrid(components, viewport).filter(
    (c) => !isComponentHidden(c, viewport),
  );

  let maxRow = Math.max(0, Number(incomingPreviewEnd) || 0);
  for (const c of visible) {
    if (forestDesktopBleed && isJardinsForestFillImage(c, section)) continue;
    const p =
      previewCompId === c.id && previewPlacement
        ? previewPlacement
        : getGridPlacement(c, viewport);
    maxRow = Math.max(maxRow, p.row + (p.rowSpan ?? 1) - 1);
  }

  const contentRows = Math.max(1, maxRow + 1);
  const sectionMinRows = resolveSectionMinGridRows(section, viewport);
  const configuredRows = resolveSectionConfiguredGridRows(section, viewport);

  if (resizePreviewRows != null) {
    return Math.max(4, resizePreviewRows);
  }

  const userLockedHeight =
    section?.gridRowsCustom === true && configuredRows > 0 ? configuredRows : 0;

  if (viewport !== 'desktop') {
    if (ignoreConfiguredRows) {
      return Math.max(contentRows, 4);
    }
    if (userLockedHeight > 0) {
      return Math.max(4, userLockedHeight);
    }
    return Math.max(contentRows, sectionMinRows, 4);
  }

  if (ignoreConfiguredRows) {
    return Math.max(contentRows, 4);
  }

  if (userLockedHeight > 0) {
    return Math.max(4, userLockedHeight);
  }

  return Math.max(contentRows, sectionMinRows, 4);
}

/** Hauteur explicite (px) quand l'utilisateur a fixé le nombre de lignes. */
export function resolveSectionFixedHeightPx(section, viewport, totalGridRows, kitRowH) {
  if (!section?.gridRowsCustom) return null;
  if (resolveSectionConfiguredGridRows(section, viewport) <= 0) return null;
  const rows = Math.max(4, Number(totalGridRows) || 0);
  const rh = Number(kitRowH) > 0 ? Number(kitRowH) : 16;
  return rows * rh;
}

/** Réduit la hauteur mobile/tablette si le contenu visible est plus petit que la hauteur enregistrée. */
export function fitSectionGridRowsToVisibleContent(section, viewport) {
  if (!section || viewport === 'desktop') return section;
  const configured = resolveSectionConfiguredGridRows(section, viewport);
  const contentRows = resolveSectionTotalGridRows(section, viewport, section.components, {
    ignoreConfiguredRows: true,
  });
  if (!configured || contentRows < configured) {
    return applySectionGridRowsForViewport(section, viewport, contentRows);
  }
  return section;
}

export function resolveSectionMinGridRows(section, viewport = 'desktop') {
  if (viewport !== 'desktop') {
    const field = viewportSectionGridRowsField(section, viewport);
    if (Number(section?.[field]) > 0) return 0;
  }

  /** Hauteur manuelle : pas de plancher imposé (évite de bloquer le resize). */
  if (section?.gridRowsCustom) return 0;

  const t =
    section?.sectionType ||
    (section?.background?.type === 'video'
      ? 'video'
      : section?.background?.type === 'image'
        ? 'image'
        : 'normal');
  if (t !== 'image' && t !== 'video') return 0;

  const w = contentWidthForViewport(viewport);
  const bg = section?.background || {};

  if (viewport !== 'desktop') {
    return 0;
  }

  if (bg.mediaWidth && bg.mediaHeight) {
    return computeGridRowsFromAspectRatio(
      bg.mediaWidth,
      bg.mediaHeight,
      w,
      gridRowHeightForSectionMeta(section),
    );
  }

  const fallback = Number(section?.gridRows);
  return fallback > 0 ? gridRowsForViewport(fallback, viewport) : 16;
}

export async function applyMediaDimensionsToSection(section, mediaUrl, kind, rowHeight) {
  const loader = kind === 'video' ? loadVideoDimensions : loadImageDimensions;
  const rowH = rowHeight > 0 ? rowHeight : GRID_ROW_HEIGHT;
  try {
    const { width, height } = await loader(mediaUrl);
    const gridRows = computeGridRowsFromAspectRatio(width, height, DESKTOP_WIDTH, rowH);
    return {
      ...section,
      gridRows,
      gridRowsCustom: false,
      background: {
        ...(section.background || {}),
        mediaWidth: width,
        mediaHeight: height,
      },
    };
  } catch {
    return section;
  }
}
